import { useState, type FormEvent } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider.js';
import { Button } from '../components/Button.js';
import { NovaOrb } from '../components/NovaOrb.js';
import { ApiError } from '../lib/api.js';

export function LoginPage() {
  const { login, status } = useAuth();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (status === 'authenticated') {
    const from = (location.state as { from?: string } | null)?.from;
    return <Navigate to={from ?? '/'} replace />;
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await login(email, password);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Klarte ikke å logge inn. Prøv igjen.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="flex min-h-dvh items-center justify-center px-5 py-10">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex flex-col items-center text-center">
          <NovaOrb size={18} />
          <h1 className="font-display text-ink mt-4 text-3xl font-bold tracking-[0.3em]">NOVA</h1>
          <p className="text-ink-soft mt-2 text-sm">Markedsdirektøren for Spama og Prokom</p>
        </div>

        <form onSubmit={handleSubmit} className="glass shadow-panel space-y-4 p-6">
          <div>
            <label htmlFor="email" className="label-micro mb-1.5 block">
              E-postadresse
            </label>
            <input
              id="email"
              type="email"
              autoComplete="username"
              required
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="border-hairline bg-base/60 text-ink placeholder:text-ink-soft/60 focus:border-accent/50 h-11 w-full rounded-lg border px-3 text-sm transition"
              placeholder="fornavn.etternavn@prokom.no"
            />
          </div>

          <div>
            <label htmlFor="password" className="label-micro mb-1.5 block">
              Passord
            </label>
            <input
              id="password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="border-hairline bg-base/60 text-ink focus:border-accent/50 h-11 w-full rounded-lg border px-3 text-sm transition"
            />
          </div>

          {error && (
            <p
              role="alert"
              className="border-danger/30 bg-danger/10 text-danger rounded-lg border px-3 py-2 text-sm"
            >
              {error}
            </p>
          )}

          <Button type="submit" variant="primary" loading={busy} className="w-full">
            Logg inn
          </Button>

          <Link
            to="/glemt-passord"
            className="text-ink-soft hover:text-ink block text-center text-xs transition"
          >
            Glemt passord?
          </Link>
        </form>

        <p className="text-ink-soft mt-6 text-center text-xs">
          Innlogging med Microsoft-konto kommer når Entra ID kobles på.
        </p>
      </div>
    </main>
  );
}
